import * as React from "react";
import { useState, useEffect, ChangeEvent } from "react";
import { connect } from "react-redux";
import { Action, bindActionCreators, Dispatch } from "redux";
import {
    currentPlan,
    currentUserEmail,
    hasAonTrustCompany,
    hasThirdPartyAccess,
    sessionPlans,
    userType
} from "../../../mainReducerMapSelectors";
import { IApiResult } from "../../api/AxiosWealthManagementApi";
import { IPlanInfo } from "../../model/ClientsAndPlans.model";
import { SharedDocument, SharedDocumentType } from "../../model/SharedDocument.model";
import { ConsultantLevelList, doesUserHaveAccess } from "../../model/UserDetails.model";
import { prominentButtonStyle, secondaryButtonStyle } from "../common/buttonStyles";
import { RaisedButton } from "../common/RaisedButton";
import { getDateByQuarter, getQuarterAndYearByDate, getQuarters, getRecentYears, Quarter } from "../../utils/dateUtil";
import { throwErrorOnNullOrUndefined } from "../../utils/errorUtil";
import { ErrorComponent } from "../base/Error.component";
import { LoadingComponent } from "../base/Loading.component";
import { ISelectValue, SelectComponent } from "../common/Select.component";
import { TextFieldComponent } from "../common/TextField.component";
import SharedDocumentsAction from "./SharedDocuments.actions";
import { ISharedDocumentsPropsFromState } from "./SharedDocuments.reducer";
import { PlanSelect } from "../upload-page/PlanSelect";
import { UserTypeEnum } from "../../model/UserInfo.model";
import { SharedDocumentApi } from "../../api/SharedDocumentApi";
import { Checkbox } from "@mui/material";
import { AonColors, LegacyColors } from "../../../../css/Colors";
import { getTranslation } from "../../utils/translationUtil";
import { IApplicationRootState } from "../../../applicationState";
import {useParams} from "react-router";
import {useNavigate} from "react-router-dom";
import {navigateTo} from "../../../navigateTo";

export interface IAdvancedEditFileComponentState {
    selectedFile: File | undefined;
    title: string;
    quarter: Quarter;
    year: number;
    selectedDocumentType: number | null;
    documentTypeError: boolean;
    documentDataPending: boolean;
    documentDataResult?: IApiResult<SharedDocument>;
    documentError: boolean;
    errorMessage: string;
    selectedPlans: number[];
    years: ISelectValue[];
    quarters: ISelectValue[];
    thirdPartyAccess: boolean;
}

export interface IAdvancedEditFilePropsFromState {
    userType: UserTypeEnum;
    currentPlan: IPlanInfo;
    sessionPlans: IPlanInfo[];
    thirdPartyEnabledPlan: boolean;
    isAtcPlan: boolean;
    currentUserEmail: string;
}

export interface IAdvancedEditFilePropsFromActions {
    actions: {
        requestDocumentTypes: typeof SharedDocumentsAction.requestDocumentTypes;
        notifyFileUpdateSuccess: typeof SharedDocumentsAction.notifyFileUpdateSuccess;
    };
}

export type AdvancedEditFilePropsFromStateCombined =
    IAdvancedEditFilePropsFromState
    & ISharedDocumentsPropsFromState;

export type IAdvancedEditFileProps =
    IAdvancedEditFilePropsFromActions
    & ISharedDocumentsPropsFromState
    & IAdvancedEditFilePropsFromState;

export const AdvancedEditFileComponent: React.FC<IAdvancedEditFileProps> = (props) => {
    const navigate = useNavigate();
    const {documentIdParam} = useParams();
    const documentId = Number.parseInt(throwErrorOnNullOrUndefined(documentIdParam), 10);
    const [state, setState] = useState<IAdvancedEditFileComponentState>({
        selectedFile: undefined,
        title: "",
        year: 1990,
        quarter: Quarter.Q1,
        selectedDocumentType: null,
        documentTypeError: false,
        documentError: false,
        documentDataPending: false,
        errorMessage: "",
        selectedPlans: [],
        years: getRecentYears(),
        quarters: getQuarters(),
        thirdPartyAccess: false,
    });

    const isCancelled = React.useRef(false);

    useEffect(() => {
        isCancelled.current = false;
        const maybeDocumentTypes = props.documentTypes.data;
        if (!maybeDocumentTypes || maybeDocumentTypes.length <= 0) {
            props.actions.requestDocumentTypes();
        }

        setState((prev) => ({ ...prev, documentDataPending: true }));

        SharedDocumentApi.getSharedDocumentData(documentId)
            .then((result) => {
                if (!isCancelled.current) {
                    setState((prev) => ({
                        ...prev,
                        documentDataResult: result,
                        documentDataPending: false,
                        ...(result.data
                            ? {
                                title: result.data.title,
                                year: result.data.date.getFullYear(),
                                quarter: getQuarterAndYearByDate(result.data.date).quarter,
                                selectedDocumentType: result.data.documentType.id,
                                documentTypeError: false,
                                selectedPlans: result.data.planIds,
                                thirdPartyAccess: result.data.thirdPartyAccess || false
                            }
                            : {})
                    }));
                }
            });

        return () => {
            isCancelled.current = true;
        };
        // eslint-disable-next-line
    }, []);

    const renderThirdPartyAccess = () => {
        return props.thirdPartyEnabledPlan
            ? <td>
                <Checkbox
                    className={`thirdPartyAccessCheckbox`}
                    onChange={handleThirdPartyAccess}
                    checked={state.thirdPartyAccess}
                    style={{ color: state.thirdPartyAccess ? AonColors.AonTealDark : LegacyColors.Black54 }}
                />
            </td>
            : null;
    };

    const renderFileTitle = () => (
        <div className="shared-document-list-combined field title edit-page">
            <TextFieldComponent
                id="title"
                name="title"
                placeholder="Enter a title for the document"
                className="title-input"
                type="text"
                fullWidth={true}
                value={state.title}
                handleChange={handleTitleChange}
                renderRequired={false}
            />
        </div>
    );

    const renderTypeDropDown = (documentTypes: SharedDocumentType[]) => (
        <div className="shared-document-list-combined field document_type edit-page">
            <SelectComponent
                id={"upload-file__document-type"}
                values={documentTypes}
                selected={state.selectedDocumentType}
                width={230}
                submitClicked={false}
                handleChange={handleDocumentTypeChange}
                placeholder={getTranslation("upload.document-type", "Document Type")}
                renderRequired={true}
            />
        </div>
    );

    const renderTimePeriodDropDowns = (widthYear: number, widthQuarter: number) => (
        <div className="shared-document-list-combined time_period">
            <div className="shared-document-list-combined field year edit-page" id="year-picker">
                <SelectComponent
                    values={state.years}
                    selected={state.year}
                    width={widthYear}
                    submitClicked={false}
                    handleChange={handleYearChange}
                    id={"edit-file__date-year"}
                    placeholder={getTranslation("upload.year", "Year")}
                    renderRequired={true}
                    selectMaxHeight={120}
                />
            </div>
            <div className="shared-document-list-combined field quarter edit-page" id="quarter-picker">
                <SelectComponent
                    values={state.quarters}
                    selected={state.quarter}
                    width={widthQuarter}
                    submitClicked={false}
                    handleChange={handleQuarterChange}
                    id={"edit-file__date-quarter"}
                    placeholder={getTranslation("upload.quarter", "Quarter")}
                    renderRequired={true}
                />
            </div>
        </div>
    );

    const handleThirdPartyAccess = (event: any) => {
        setState((prev) => ({ ...prev, thirdPartyAccess: event.target.checked }));
    };

    const onSelectPlansChange = (planIds: number[]) => {
        setState((prev) => ({ ...prev, selectedPlans: planIds }));
    };

    const accessibleToUser = () => {
        return doesUserHaveAccess(props.userType, ConsultantLevelList)
            || props.currentUserEmail === state.documentDataResult?.data?.email
            || (props.isAtcPlan && props.userType === UserTypeEnum.ATC_ADMIN);
    };

    const handleYearChange = (e: any) => {
        setState((prev) => ({ ...prev, year: e.target.value }));
    };

    const handleQuarterChange = (e: any) => {
        setState((prev) => ({ ...prev, quarter: e.target.value }));
    };

    const getFilename = (): any => {
        let fileName: string | null = null;
        if (state.documentDataResult && state.documentDataResult.data) {
            fileName = state.documentDataResult.data.fileName;
        }
        if (!fileName) {
            return;
        }
        return fileName;
    };

    const renderDocumentError = () => {
        if (state.documentError) {
            return <div id="upload-error">
                <div className="upload-error-logo" />
                <div>{state.errorMessage}</div>
            </div>;
        }
    };

    const handleTitleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setState((prev) => ({ ...prev, title: e.target.value }));
    };

    const handleDocumentTypeChange = (e: any) => {
        setState((prev) => ({ ...prev, selectedDocumentType: e.target.value, documentTypeError: false }));
    };

    const submitForm = () => {
        if (state.selectedPlans.length === 0) {
            return;
        }
        const noDocumentType = state.selectedDocumentType == null;
        if (noDocumentType) {
            setState((prev) => ({ ...prev, documentTypeError: true }));
        }
        if (noDocumentType) {
            return;
        }
        const docType = props.documentTypes.data!
            .find((x) => x.id === state.selectedDocumentType) as SharedDocumentType;
        const sharedDocumentData = state.documentDataResult!.data!;
        const sharedDocument = new SharedDocument(
            sharedDocumentData.id,
            sharedDocumentData.fileName,
            state.title,
            getDateByQuarter(state.quarter, state.year),
            docType,
            sharedDocumentData.createdMillis,
            state.selectedPlans,
            sharedDocumentData.email,
            state.thirdPartyAccess
        );
        SharedDocumentApi.updateSharedDocument(sharedDocument)
            .then(() => {
                setState((prev) => ({ ...prev, documentError: false }));
                props.actions.notifyFileUpdateSuccess();
                navigateTo("/shared-documents", navigate);
            }, () => {
                setState((prev) => ({ ...prev, documentError: true, errorMessage: "File save unsuccessful" }));
            });
    };

    const documentTypes = props.documentTypes.data || [];
    const widthYear = 100;
    const widthQuarter = 100;

    if (state.documentDataPending) {
        return <LoadingComponent />;
    }
    if (props.documentTypes.error) {
        return <ErrorComponent />;
    }
    if (state.documentDataResult && state.documentDataResult.error) {
        return <ErrorComponent />;
    }
    if (!accessibleToUser()) {
        return <ErrorComponent />;
    }

    return (
        <div id="container" className="main-content" data-testid="shared-document-edit">
            <h1 id="page-title">Edit File - {props.currentPlan.name}</h1>
            <div className="shared-document-list-combined">
                <table style={{ marginLeft: "-35px" }}>
                    <thead>
                    <tr>
                        <th>
                            <div>FILENAME</div>
                        </th>
                        <th>
                            <div>DOCUMENT TITLE</div>
                        </th>
                        <th>
                            <span>DOCUMENT TYPE</span>
                        </th>
                        <th>
                            <span>TIME PERIOD</span>
                        </th>
                        {props.thirdPartyEnabledPlan &&
                            <th>
                                <span>3RD PARTY ACCESS</span>
                            </th>
                        }
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td style={{ width: "150px" }}>
                            <div className="shared-document-list-combined filename">
                                <div id="filename">{getFilename()}</div>
                                <div style={{ minHeight: "15px" }} />
                            </div>
                        </td>
                        <td>
                            {renderFileTitle()}
                        </td>
                        <td style={{ width: "260px" }}>
                            {renderTypeDropDown(documentTypes)}
                        </td>
                        <td style={{ width: "230px" }}>
                            {renderTimePeriodDropDowns(widthYear, widthQuarter)}
                        </td>
                        {renderThirdPartyAccess()}
                    </tr>
                    </tbody>
                </table>
                <PlanSelect
                    onSelectElementsCallBack={onSelectPlansChange}
                    selectedElements={state.selectedPlans}
                    currentPlan={props.currentPlan}
                    userType={props.userType}
                >
                    <div className="plans_header">PLANS</div>
                </PlanSelect>
                <div className="shared-document-list-combined submit_cancel_buttons">
                    <RaisedButton
                        className="edit-file__cancel-button"
                        style={{ ...secondaryButtonStyle }}
                        primary={false}
                        onClick={() => {navigateTo("/shared-documents", navigate);}}>
                        Cancel
                    </RaisedButton>
                    <RaisedButton
                        className="edit-file__save-button"
                        style={{ ...prominentButtonStyle, marginLeft: 15 }}
                        primary={true}
                        onClick={submitForm}>
                        Save Changes
                    </RaisedButton>
                </div>
            </div>
            {renderDocumentError()}
        </div>
    );
};

export const mapStateToProps = (state: IApplicationRootState):
    ISharedDocumentsPropsFromState & IAdvancedEditFilePropsFromState => {
    return {
        ...state.sharedDocumentsRootState!,
        userType: userType(state),
        currentPlan: currentPlan(state),
        sessionPlans: sessionPlans(state),
        currentUserEmail: currentUserEmail(state),
        thirdPartyEnabledPlan: hasThirdPartyAccess(state),
        isAtcPlan: hasAonTrustCompany(state)
    };
};

export const mapDispatchToProps = (dispatch: Dispatch<Action>): IAdvancedEditFilePropsFromActions => {
    return {
        actions: bindActionCreators({
            requestDocumentTypes: SharedDocumentsAction.requestDocumentTypes,
            notifyFileUpdateSuccess: SharedDocumentsAction.notifyFileUpdateSuccess,
        }, dispatch),
    };
};

export default connect<AdvancedEditFilePropsFromStateCombined,
    IAdvancedEditFilePropsFromActions,
    void>(mapStateToProps, mapDispatchToProps)(AdvancedEditFileComponent);